import {APIClient, setAuthorization} from "../apiCore";
import * as url from "../urls";
import {defaultBrowserFingerPrintInputProps, getBrowserFingerPrintData} from "../../components/FingerPrint/browser";
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type { AuthenticationResponseJSON, RegistrationResponseJSON } from '@simplewebauthn/browser';
import {MainUserSignUpPasskeyType} from "../../redux/Main/User/types";

const api = new APIClient();

const postJwtLogin = async(data: any) => {
    setAuthorization(null);

    const response = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        ...data,
        device: response
    }

    return api.create(url.POST_FAKE_JWT_LOGIN, sendData);
}

const getWebauthnStartOptions = async(data: any) => {

    const device = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        ...data,
        device
    };

    return await api.create(url.WEBAUTHN_LOGIN_OPTIONS, sendData);
}


class PasskeyAutofillNotCompleted extends Error {
    constructor(readonly reason: unknown) {
        super('passkeyAutofillNotCompleted');
        this.name = 'PasskeyAutofillNotCompleted';
    }
}

const postPasskeyLogin = async(data: any) => {
    setAuthorization(null);
    const device = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        ...data,
    };

    const optionsResp: any = await getWebauthnStartOptions(sendData);

    let assertion: AuthenticationResponseJSON;
    try {
        assertion = await startAuthentication({ 
            optionsJSON: optionsResp,
            useBrowserAutofill: data.useBrowserAutofill 
        });
    } catch (error) {
        // Autofill runs in the background, unasked: a ceremony that never
        // completed (aborted on leaving the form, no matching input…) is not
        // an error to show the user.
        if (data.useBrowserAutofill) {
            throw new PasskeyAutofillNotCompleted(error);
        }
        throw error;
    }

    let resultData = {
        ...data,
        ...assertion,
        device
    };

    return api.create(url.WEBAUTHN_LOGIN_RESULT, resultData);
}

const postPasskeyRegister = async(data: MainUserSignUpPasskeyType) => {
    const device = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        ...data,
        username: data.email,
        displayName: data.email,
    };

    const optionsResp: any = await api.create(url.WEBAUTHN_REGISTER_OPTIONS, sendData);

    let attestation: RegistrationResponseJSON;

    try {
        attestation = await startRegistration({ optionsJSON: optionsResp });
    } catch (error) {
        throw error;
    }

    let resultData = {
        ...data,
        ...attestation,
        device
    };

    return api.create(url.WEBAUTHN_REGISTER_RESULT, resultData);
}

const postAddPasskey = async(data: any = {}) => {
    const device = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        ...data,
        username: data.email,
        displayName: data.email,
        device
    };

    const optionsResp: any = await api.create(url.WEBAUTHN_PASSKEY_ADD_OPTIONS, sendData);

    let attestation: RegistrationResponseJSON;

    try {
        attestation = await startRegistration({ optionsJSON: optionsResp });
    } catch (error) {
        throw error;
    }

    let resultData = {
        ...data,
        ...attestation,
        device
    };

    return api.create(url.WEBAUTHN_PASSKEY_ADD_RESULT, resultData);
}

const socialCheck = async(data: any) => {
    setAuthorization(null);

    const response = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        ...data,
        device: response
    }

    return api.create(url.SOCIAL_CHECK, sendData);
}

const logoutUser = async() => {
    const response = await getBrowserFingerPrintData(defaultBrowserFingerPrintInputProps);

    let sendData = {
        device: response
    }

    return api.create(url.AUTH_LOGOUT, sendData);
}

const getJWTToken = () => {
    return api.get(url.GET_JWT_KEY_AFTER_LOGIN);
}


export {
    PasskeyAutofillNotCompleted,
    postJwtLogin,
    getWebauthnStartOptions,
    postPasskeyLogin,
    postPasskeyRegister,
    postAddPasskey,
    logoutUser,
    getJWTToken,
    socialCheck
};
